// 백엔드와 이야기하는 유일한 곳. 봉투 파싱·request_id·401 처리가 여기 하나에만 있다.

export type ErrorCode =
  | 'invalid_request'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'internal'
  | 'upstream_unavailable'
  | 'machine_unreachable'
  | 'unavailable'
  | 'too_many_requests'

export class ApiError extends Error {
  readonly status: number
  readonly code: ErrorCode | 'network' | 'unknown'
  readonly requestId: string | null
  /** 409 가 어떤 조건에 거부됐는지 말하는 기계용 코드. 없으면 null. */
  readonly reason: string | null
  /** 봉투 밖에 실려 온 거절의 사실(노드 재부팅의 `guests` 처럼). 없으면 null. */
  readonly body: unknown

  constructor(status: number, code: ApiError['code'], message: string, requestId: string | null, reason: string | null = null, body: unknown = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.requestId = requestId
    this.reason = reason
    this.body = body
  }
}

// API 클라이언트가 명시적으로 넣은 Bearer 토큰은 여기에만 있다. 제품 브라우저는
// 백엔드의 HttpOnly 쿠키를 쓰며 JS에서 세션 값을 읽거나 저장하지 않는다.
let token: string | null = null
let onUnauthorized: (() => void) | null = null

export function setToken(t: string | null) {
  token = t
}

// 401 은 호출부마다 흩어지지 않고 여기서 한 번만 처리한다.
export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn
}

type Options = {
  method?: string
  body?: unknown
  query?: Record<string, string | number | undefined>
  noAuth?: boolean
}

function withQuery(path: string, query: Options['query']): string {
  if (!query) return path
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined) qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `${path}?${s}` : path
}

async function send(path: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(path, init)
  } catch {
    throw new ApiError(0, 'network', '연결할 수 없습니다.', null)
  }
}

/** 실패 응답을 ApiError 로. 봉투가 아닌 평문(/install/*, 다운로드)도 견딘다. 401 은 여기서 한 번만 처리한다. */
async function fail(res: Response, noAuth: boolean): Promise<never> {
  const text = await res.text()
  let code: ApiError['code'] = 'unknown'
  let message = text.trim() || `HTTP ${res.status}`
  let id = res.headers.get('X-Request-Id')
  let reason: string | null = null
  let parsed: unknown = null
  try {
    const body = JSON.parse(text) as {
      error?: { code?: string; message?: string; request_id?: string; reason?: string }
    }
    parsed = body
    if (body.error) {
      code = (body.error.code as ErrorCode) ?? 'unknown'
      message = body.error.message ?? message
      id = body.error.request_id ?? id
      reason = body.error.reason ?? null
    }
  } catch {
    // 평문 응답. 위에서 잡은 값 그대로 쓴다.
  }
  if (res.status === 401 && !noAuth) {
    setToken(null)
    onUnauthorized?.()
  }
  throw new ApiError(res.status, code, message, id, reason, parsed)
}

export async function request<T>(path: string, opts: Options = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  const method = opts.method ?? 'GET'
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'
  if (token && !opts.noAuth) headers['Authorization'] = `Bearer ${token}`

  // SameSite만 믿지 않는다. 쿠키로 인증되는 쓰기는 단순 HTML form이
  // 만들 수 없는 헤더를 요구하고, API의 인증 미들웨어가 같은 값을 확인한다.
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) headers['X-CSRF-Protection'] = '1'

  const res = await send(withQuery(path, opts.query), {
    method,
    headers,
    credentials: 'same-origin',
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
  })

  if (res.status === 204) return undefined as T
  if (!res.ok) return fail(res, opts.noAuth ?? false)

  const text = await res.text()
  if (!text) return undefined as T
  return JSON.parse(text) as T
}

/** 파일을 실어 보내는 쓰기(multipart). Content-Type 은 브라우저가 경계와 함께 정한다. */
export async function upload<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', 'X-CSRF-Protection': '1' }
  if (token) headers['Authorization'] = `Bearer ${token}`
  const res = await send(path, { method: 'POST', headers, credentials: 'same-origin', body: form })
  if (!res.ok) return fail(res, false)
  return JSON.parse(await res.text()) as T
}

export type Download = { blob: Blob; filename: string | null }

/**
 * 인증된 단회 파일 내려받기. 세션 bearer 를 붙여 받고, 캐시에 남기지 않는다.
 * 호출자는 돌아온 Blob 을 곧바로 저장하고 버린다 — 링크도 내용도 state 에 두지 않는다.
 * 백엔드가 만든 접속용 개인키(`ssh_private_key_download_url`)가 이 길을 지난다: 링크는 한 번만 열린다.
 */
export async function downloadBlob(path: string): Promise<Download> {
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  const res = await send(path, { headers, cache: 'no-store', credentials: 'same-origin' })
  if (!res.ok) return fail(res, false)
  return { blob: await res.blob(), filename: filenameOf(res.headers.get('Content-Disposition')) }
}

/** Content-Disposition 의 파일 이름. 없으면 null — 호출자가 대신 이름을 정한다. */
export function filenameOf(disposition: string | null): string | null {
  if (!disposition) return null
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition)
  if (star?.[1]) return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''))
  const plain = /filename="?([^";]+)"?/i.exec(disposition)
  return plain?.[1]?.trim() || null
}

/** Blob 을 브라우저 저장으로 넘기고 객체 URL 을 바로 거둔다. 사람이 저장할 뿐, 화면은 아무것도 갖지 않는다. */
export function saveBlob(d: Download, fallback: string) {
  const url = URL.createObjectURL(d.blob)
  const a = document.createElement('a')
  a.href = url
  a.download = d.filename ?? fallback
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
